import { useUserStore } from "../../store/userStore";
import { useActiveAppStore } from "../../store/activeAppStore";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useTruckYardStore } from "../../store/truckYardStore";
import {
  useTruckYardCounts,
  type TruckYardCounts,
} from "../../features/truckYard/useTruckYardCounts";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import TruckYardHeader from "./Header";
import {
  AppsSidebar,
  useFilteredCategories,
} from "../../components/base/AppsSidebar";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";
import { brand } from "../../config/theme";
import { incrementAdminFetchGeneration } from "../../utils/adminFetchGeneration";
import { setAdminAbortSignal } from "../../utils/api";
import { faWarehouse } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import type { SidebarMenuItem } from "../../components/base/AppSidebarMenu";
import CompanySelector from "../../components/base/CompanySelector";
import { ADMIN_RESELLER_ROLE_ID } from "../../hooks/useIsReseller";
import { allowedTruckYardSections } from "./truckYardSectionAccess";
import {
  PTAR_COMPANY_ID,
  TRUCK_YARD_GUARD_ROLE_ID,
  getUserAssignedYardIds,
  getAssignedYardsLabel,
  useTruckYardRole,
} from "../../hooks/useTruckYardRole";
import { useAccessValidation } from "../../hooks/useAccessValidation";
import { useSidebarCollapsed } from "../../hooks/useSidebarCollapsed";

export default function TruckYardLayout() {
  const user = useUserStore((state) => state.user);
  const filteredCategories = useFilteredCategories();
  const hasRedirected = useRef(false);

  const {
    isExpiringSoon,
    timeRemainingFormatted,
    isExpired,
    isAuthenticated,
    isInitialized,
  } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  // Continuous access validation for guards and supervisors
  // Checks geofence and schedule periodically
  useAccessValidation({ enabled: true });

  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const { isCollapsed: isSidebarCollapsed, toggleCollapsed: toggleSidebar } =
    useSidebarCollapsed("truckYardSidebarCollapsed", {
      collapseOnSmallScreens: true,
    });

  const handleToggleSubmenu = (menuId: string) => {
    setExpandedMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId],
    );
  };

  // Company logic for TruckYard
  const companyId = useCompaniesStore((state) => state.companyId);
  const setCompanyId = useCompaniesStore((state) => state.setCompanyId);
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const fetchAllCompanies = useCompaniesStore(
    (state) => state.fetchAllCompanies,
  );

  const counts = useTruckYardCounts(companyId);
  const countsData: Partial<TruckYardCounts> = counts.data ?? {};
  const countsLoading = counts.isLoading;

  // Get assigned yard name for supervisors/inspectors (not admins - they can have multiple yards)
  const { hasYardRestrictions } = useTruckYardRole();
  const yards = useTruckYardStore((state) => state.yards);

  // Un solo campo para los cuatro roles (issue #497): el respaldo al singular
  // que antes se resolvía acá vive ahora dentro de getUserAssignedYardIds.
  const assignedYardIds = getUserAssignedYardIds();
  const [assignedYardName, setAssignedYardName] = useState(
    localStorage.getItem("userYardName") || "",
  );

  // Get assigned yard name (only for users with yard restrictions).
  // El `length > 0` de acá NO es de los que apagaban el recorte (issue #497):
  // sólo evita buscar un nombre de patio cuando no hay ningún patio.
  useEffect(() => {
    if (
      hasYardRestrictions() &&
      assignedYardIds.length > 0 &&
      yards.length > 0
    ) {
      // Con varios patios no se nombra uno arbitrario: ver getAssignedYardsLabel.
      const label = getAssignedYardsLabel(assignedYardIds, yards);
      // Only update if we found a yard, otherwise keep the previous value
      if (label) {
        setAssignedYardName(label);
        localStorage.setItem("userYardName", label);
      }
    }
  }, [hasYardRestrictions, assignedYardIds, yards]);

  // Compañía por defecto del patio: PTAR, el cliente de patios de Dumax, pero
  // SÓLO para el admin reseller (el único con selector de compañías). Antes se
  // fijaba para todo usuario y además se pisaba `companyUser`: un usuario de
  // otra compañía quedaba mandando `x-company-id` de PTAR y leía sus
  // controladores. El servidor ahora amarra la cabecera al usuario
  // (hikvisionAccessAuthz.js); cualquier otro usa la compañía de su sesión.
  useEffect(() => {
    if (localStorage.getItem("userRoleId") === ADMIN_RESELLER_ROLE_ID) {
      setCompanyId(PTAR_COMPANY_ID);
    }
  }, [setCompanyId]);

  // Fetch all companies for selector
  useEffect(() => {
    fetchAllCompanies();
  }, [fetchAllCompanies]);

  // Check if user is reseller or truck yard role
  const userRoleId = localStorage.getItem("userRoleId") || "";
  const isReseller = userRoleId === ADMIN_RESELLER_ROLE_ID;
  const isTruckYardGuard = userRoleId === TRUCK_YARD_GUARD_ROLE_ID;

  const setActiveApp = useActiveAppStore((state) => state.setActiveApp);
  useEffect(() => {
    setActiveApp(
      "truck-yard",
      t("apps.truckYard.name"),
      t("apps.truckYard.description"),
      faWarehouse,
      "bg-teal-500",
    );
  }, [setActiveApp, t]);

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const navigate = useNavigate();
  const location = useLocation();

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  const handleAppSelect = (app: AppItem) => {
    if (app.id === "truck-yard") {
      closeAppsSidebar();
      return;
    }

    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    if (
      isInitialized &&
      (isExpired || !isAuthenticated) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user) {
      if (location.pathname !== "/splash") {
        localStorage.setItem("lastRoute", location.pathname);
        navigate("/splash");
      }
    }
  }, [user, location.pathname, navigate]);

  // Cleanup abort signal on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setAdminAbortSignal(null);
      incrementAdminFetchGeneration();
    };
  }, []);

  // Three top-level sections, each with sub-items. Parents have no `count`
  // and clicking them only toggles their submenu (see AppSidebarMenu).
  const patiosChildren = [
    {
      id: "yards",
      label: t("sidebarMenu.truckYard.patios"),
      icon: "ri-building-4-line",
      path: "/truck-yard/yards",
      count: countsData.yards ?? 0,
      loading: countsLoading,
    },
    {
      id: "vehicles",
      label: t("sidebarMenu.truckYard.vehicles"),
      icon: "ri-truck-line",
      path: "/truck-yard/vehicles",
      count: countsData.vehicles ?? 0,
      loading: countsLoading,
    },
    {
      id: "groups",
      label: t("sidebarMenu.truckYard.groups"),
      icon: "ri-folder-user-line",
      path: "/truck-yard/groups",
      count: countsData.groups ?? 0,
      loading: countsLoading,
    },
  ];

  const accessControlChildren = [
    {
      id: "access",
      label: t("sidebarMenu.truckYard.access"),
      icon: "ri-door-lock-line",
      path: "/truck-yard/access",
      count: countsData.accessEvents ?? 0,
      loading: countsLoading,
    },
    {
      id: "access-control",
      label: t("sidebarMenu.truckYard.accessDevices"),
      icon: "ri-router-line",
      path: "/truck-yard/access-control",
      count: countsData.accessDevices ?? 0,
      loading: countsLoading,
    },
    {
      id: "access-cards",
      label: t("sidebarMenu.truckYard.accessCards"),
      icon: "ri-bank-card-line",
      path: "/truck-yard/access-cards",
      count: countsData.accessCards ?? 0,
      loading: countsLoading,
    },
    {
      id: "relays",
      label: t("sidebarMenu.truckYard.relays"),
      icon: "ri-router-line",
      path: "/truck-yard/relays",
    },
  ];

  const personalItem: SidebarMenuItem = {
    id: "personal",
    label: t("sidebarMenu.truckYard.personal"),
    icon: "ri-team-line",
    path: "/truck-yard/personal",
    count:
      (countsData.guards ?? 0) +
      (countsData.supervisors ?? 0) +
      (countsData.inspectors ?? 0) +
      (countsData.admins ?? 0) +
      (countsData.yardAdmins ?? 0),
    loading: countsLoading,
  };

  // Role gating operates on child IDs; parents are kept if at least one
  // child survives the filter. The flat "personal" item is gated separately.
  // Misma regla que aplica el router (TruckYardRoleRoute): ver
  // truckYardSectionAccess.ts. null = admin/reseller, sin restricción.
  const allowedChildIds = allowedTruckYardSections(userRoleId);

  const filterChildren = <T extends { id: string }>(children: T[]): T[] =>
    allowedChildIds
      ? children.filter((c) => allowedChildIds.has(c.id))
      : children;

  // Supervisors can manage guards via Personal; guards have no Personal access.
  const showPersonal = !isTruckYardGuard;

  type ParentSection = {
    id: string;
    label: string;
    icon: string;
    children: {
      id: string;
      label: string;
      icon: string;
      path: string;
      count?: number;
      loading?: boolean;
    }[];
  };

  const parentSections: ParentSection[] = [
    {
      id: "patios",
      label: t("sidebarMenu.truckYard.patios"),
      icon: "ri-community-line",
      children: filterChildren(patiosChildren),
    },
    {
      id: "access-control-group",
      label: t("sidebarMenu.truckYard.accessControl"),
      icon: "ri-shield-keyhole-line",
      children: filterChildren(accessControlChildren),
    },
  ].filter((section) => section.children.length > 0);

  const menuItems: SidebarMenuItem[] = [
    ...parentSections,
    ...(showPersonal ? [personalItem] : []),
  ];

  // Auto-expand the parent section whose child matches the current route, so
  // a deep link or refresh on /truck-yard/access lands with "Access Control"
  // open instead of all sections collapsed.
  useEffect(() => {
    const activeParent = parentSections.find((section) =>
      section.children.some(
        (child) =>
          location.pathname === child.path ||
          location.pathname.startsWith(child.path + "/"),
      ),
    );
    if (activeParent && !expandedMenus.includes(activeParent.id)) {
      setExpandedMenus((prev) => [...prev, activeParent.id]);
    }
    // We intentionally depend only on the pathname; section identities are
    // stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const totalElementos =
    parentSections.reduce(
      (acc, section) =>
        acc +
        section.children.reduce(
          (childAcc, child) =>
            typeof child.count === "number" && !child.loading
              ? childAcc + child.count
              : childAcc,
          0,
        ),
      0,
    ) +
    (showPersonal &&
    !personalItem.loading &&
    typeof personalItem.count === "number"
      ? personalItem.count
      : 0);

  return (
    <div
      className={`h-screen flex flex-col ${isDarkMode ? "bg-[#1A1A1A]" : "bg-gray-50"}`}
    >
      {/* Banner de sesión por expirar */}
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>
            {t("sessionBanner.expiring", { time: timeRemainingFormatted })}
          </span>
        </div>
      )}

      <TruckYardHeader
        onOpenAppsSidebar={openAppsSidebar}
        onToggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
        isDarkMode={isDarkMode}
        companies={allCompanies}
        selectedCompany={companyId}
        onCompanyChange={setCompanyId}
        isReseller={isReseller}
      />
      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        <AppSidebarMenu
          menuItems={menuItems}
          isDarkMode={isDarkMode}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={closeMobileMenu}
          onNavigate={(path) => {
            navigate(path);
          }}
          isActiveRoute={(itemId, path) => {
            if (path) {
              return (
                location.pathname === path ||
                location.pathname.startsWith(path + "/")
              );
            }
            return location.pathname.endsWith(itemId);
          }}
          expandedMenus={expandedMenus}
          onToggleSubmenu={handleToggleSubmenu}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          companySelector={
            isReseller ? (
              <CompanySelector
                value={companyId ?? null}
                onChange={(id) => setCompanyId(id)}
                isDarkMode={isDarkMode}
                isClearable
                companies={allCompanies.length > 0 ? allCompanies : undefined}
                className="w-full"
              />
            ) : undefined
          }
          companyIndicator={{
            companyId,
            companyName:
              allCompanies.find((c) => c.id === companyId)?.name ||
              allCompanies.find((c) => c.id === companyId)?.business_name ||
              companyId,
            yardName: assignedYardName,
            isReseller,
            isDarkMode,
          }}
          footerStats={{
            label: "Resumen",
            items: [{ label: "Total elementos", value: totalElementos }],
          }}
        />
        {/* Main Content */}
        <div className="flex-1 max-h-full overflow-hidden min-w-0 w-full">
          <Outlet />
        </div>
      </div>
      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="truck-yard"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        isDarkMode={isDarkMode}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
